import { Box, Typography, Chip } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { useTranslate, useGetMany, ImageField } from 'react-admin';
import {
    summaryStepChipStyles,
    summaryStepContainerStyles,
    summaryStepTypographyStyles,
    summaryStepImageStyles,
    summaryStepLayoutStyles,
} from '@/themes/commonStyles';
import { CampaignProductScope } from '@/constants/CampaignProductScope';
import { NoImage } from '@/components/global/fields/NoImage';
import { useProductBrands } from '@/composables/useProductBrands';
import { useMemo } from 'react';

const SummaryStepProductsInfo = () => {
    const translate = useTranslate();
    const productScope = useWatch({ name: 'productScope' });
    const brandIds = useWatch({ name: 'brandIds' });
    const categoryIds = useWatch({ name: 'categoryIds' });
    const productId = useWatch({ name: 'productId' });

    const { data: brandNames = [] } = useProductBrands();
    const brandChoices = useMemo(() => brandNames.map((name, idx) => ({ id: idx + 1, name })), [brandNames]);

    const brands = useMemo(() => {
        const ids = brandIds || [];
        return ids
            .map(id => {
                const choice = brandChoices.find(c => c.id === id);
                return choice ? { id, brand: choice.name } : null;
            })
            .filter(Boolean);
    }, [brandIds, brandChoices]);

    const { data: categories = [] } = useGetMany('product-categories-mock', {
        ids: categoryIds || [],
    });
    const { data: product = [] } = useGetMany('product-mock', {
        ids: productId ? [productId] : [],
    });

    const singleProduct = product?.[0];

    if (productScope === CampaignProductScope.ALL) {
        return <Chip label={translate('campaign.scope.all')} sx={summaryStepChipStyles.primary} />;
    }

    if (productScope === CampaignProductScope.GROUP) {
        return (
            <Box sx={summaryStepLayoutStyles.flexRow}>
                {brands.map(brand => (
                    <Chip
                        key={`brand-${brand.id}`}
                        label={
                            <Box>
                                <span style={{ fontWeight: 700 }}>{translate('resources.campaign.labels.brandLabel')}</span> {brand.brand}
                            </Box>
                        }
                        sx={summaryStepChipStyles.secondary}
                    />
                ))}
                {categories.map(category => (
                    <Chip
                        key={`category-${category.id}`}
                        label={category.name}
                        sx={{
                            ...summaryStepChipStyles.primary,
                            backgroundColor: category.colorDark,
                            fontWeight: 400,
                        }}
                    />
                ))}
            </Box>
        );
    }

    if (productScope === CampaignProductScope.SINGLE && singleProduct) {
        return (
            <Box sx={summaryStepContainerStyles.productCard}>
                <Box sx={summaryStepContainerStyles.imageContainer}>
                    {singleProduct.image ? (
                        <ImageField source='image' title={singleProduct.name} record={singleProduct} sx={summaryStepImageStyles.productImage} />
                    ) : (
                        <NoImage sx={summaryStepImageStyles.noImage} alt={singleProduct.name} />
                    )}
                </Box>
                <Box sx={summaryStepContainerStyles.contentContainer}>
                    <Typography variant='body2' sx={summaryStepTypographyStyles.productName}>
                        {singleProduct.name}
                    </Typography>
                    <Typography variant='caption' sx={summaryStepTypographyStyles.productEan}>
                        {translate('resources.campaign.labels.ean')} {singleProduct.ean}
                    </Typography>
                </Box>
            </Box>
        );
    }

    return null;
};

export default SummaryStepProductsInfo;
